/**
 * How two devices find each other, and what the service in the middle is
 * allowed to see.
 *
 * The promise on the website will be: no strangers in the chain, and the
 * service can never read content. The second half of that is a property of
 * this file as much as of the encryption. A data channel is encrypted between
 * the two devices by WebRTC itself, so the service could not read it even if
 * it wanted to. What the service *could* see is anything we hand it, so
 * nothing but signalling is ever handed to it, and that is enforced here
 * rather than left to whoever writes the next feature.
 *
 * The exchange:
 *
 *   1. The desktop opens a room at the service and puts the room and its
 *      secret in the QR.
 *   2. The phone scans and joins that room with that secret.
 *   3. They swap the two things WebRTC needs, a description and a list of
 *      candidate addresses, through the room.
 *   4. The channel comes up between them, direct if the networks allow it and
 *      through the service's relay if they do not. Either way encrypted end
 *      to end.
 *
 * After step 4 nothing goes through the room again.
 */

export const PROTOCOL = "novamund-transport/1";

/**
 * The only things that may cross the service. Anything else is a bug.
 *
 * "arrived" and "ready" are two different moments that used to share one
 * word, and that cost three failed tests and most of an evening on
 * 2026-07-26. The service says "arrived" when the second device joins the
 * room. A device says "ready" once the channel between the two is open.
 * Reading the first as the second told the desktop it was already connected,
 * so it ignored the offer that came next as out of turn and never answered.
 * Both screens then blamed the network.
 */
export const SIGNAL_KINDS = ["description", "candidate", "arrived", "ready", "bye"];

/** Rooms are short-lived. A room nobody joined is worthless within minutes. */
export const ROOM_TTL_SECONDS = 300;

export class SignallingError extends Error {}

/**
 * Everything the service is ever given, in one place.
 *
 * Kept small on purpose: room, role, and the payload WebRTC needs. No account
 * id, no device name, no conversation, nothing that would make the service
 * worth reading.
 */
export function encodeSignal({ room, role, kind, payload }) {
  if (!room) throw new SignallingError("A signal needs a room.");
  if (role !== "host" && role !== "guest") {
    throw new SignallingError("A signal comes from the host or the guest.");
  }
  if (!SIGNAL_KINDS.includes(kind)) {
    // The guard that keeps the promise true. Application data has no kind
    // here, so it cannot be sent by accident.
    throw new SignallingError(`Refusing to send "${kind}" through the service.`);
  }
  return JSON.stringify({ v: PROTOCOL, room, role, kind, payload: payload ?? null });
}

/** The other direction, refusing anything that is not our protocol. */
export function decodeSignal(raw) {
  let message;
  try {
    message = JSON.parse(raw);
  } catch {
    throw new SignallingError("That is not a signal.");
  }
  if (!message || message.v !== PROTOCOL) {
    throw new SignallingError("That is not a signal of a version we speak.");
  }
  if (!SIGNAL_KINDS.includes(message.kind)) {
    throw new SignallingError(`Unknown signal "${message.kind}".`);
  }
  if (message.role !== "host" && message.role !== "guest") {
    throw new SignallingError("A signal comes from the host or the guest.");
  }
  return message;
}

/**
 * The room, as it goes in the QR.
 *
 * The secret is what stops someone who guesses a room id from joining it. It
 * rides in the fragment of the scanned url, exactly like the pairing secret,
 * so it never reaches a server log.
 */
export function roomUrl(serviceUrl, room, secret) {
  return `${serviceUrl.replace(/\/+$/, "")}/join#room=${encodeURIComponent(room)}&key=${encodeURIComponent(secret)}`;
}

export function readRoomFromHash(hash) {
  const params = new URLSearchParams((hash || "").replace(/^#/, ""));
  const room = params.get("room");
  const key = params.get("key");
  return room && key ? { room, secret: key } : null;
}

/**
 * Who speaks first, and what a side does with what arrives.
 *
 * A state machine rather than callbacks threaded through the WebRTC code,
 * because the ordering rules are the part that is easy to get subtly wrong
 * and impossible to notice until two devices on different networks disagree.
 *
 * The host waits. The guest is the one that arrives, so the guest asks and
 * the host answers. That way there is never a moment where both have made an
 * offer and neither will take the other's.
 */
export function createHandshake(role) {
  if (role !== "host" && role !== "guest") {
    throw new SignallingError("A handshake side is the host or the guest.");
  }
  let state = "waiting";
  let peerSeen = false;

  return {
    get state() {
      return state;
    },
    /** True when this side is the one that should make the offer. */
    shouldOffer() {
      return role === "guest" && peerSeen && state === "waiting";
    },
    peerArrived() {
      peerSeen = true;
      return this;
    },
    /** What to do with an arriving signal. Returns the action, never acts. */
    receive(message) {
      if (message.role === role) {
        // Rooms hold two sides. A message from our own role means someone
        // else is in here claiming to be us.
        return { action: "reject", reason: "duplicate-role" };
      }
      if (message.kind === "bye") {
        state = "closed";
        return { action: "close" };
      }
      if (message.kind === "description") {
        if (role === "host" && state === "waiting") {
          state = "answering";
          return { action: "answer" };
        }
        if (role === "guest" && state === "offering") {
          state = "connecting";
          return { action: "accept" };
        }
        return { action: "ignore", reason: "out-of-turn" };
      }
      if (message.kind === "candidate") {
        // Candidates keep arriving after the descriptions are exchanged, and
        // some arrive before. Both are normal.
        return { action: "add-candidate" };
      }
      if (message.kind === "arrived") {
        // The service saying the other device joined the room. It is not a
        // connection; it is the moment the guest starts asking.
        peerSeen = true;
        return { action: "peer-arrived" };
      }
      if (message.kind === "ready") {
        if (state === "waiting") {
          // An older service, or an older app, that still uses one word for
          // both moments. Treat it as the harmless one: a side that has not
          // exchanged a description yet is certainly not connected, and
          // believing otherwise is what made it ignore the offer.
          peerSeen = true;
          return { action: "peer-arrived" };
        }
        state = "connected";
        return { action: "connected" };
      }
      return { action: "ignore" };
    },
    offered() {
      state = "offering";
      return this;
    },
    answered() {
      state = "connecting";
      return this;
    },
  };
}

/**
 * A fresh room. The id is only an identifier and may be seen; the secret is
 * what stops someone who guessed the id from joining, so it comes from the
 * same source as any other key material and never from Math.random.
 */
export function newRoom(cryptoObject = window.crypto) {
  const bytes = cryptoObject.getRandomValues(new Uint8Array(24));
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return {
    room: cryptoObject.randomUUID(),
    secret: btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
  };
}

/** Where the signalling socket lives. One place, so it is checkable. */
export const CONNECT_URL =
  process.env.REACT_APP_CONNECT_URL || "https://connect-919113116762.europe-west1.run.app";

export function socketUrl(role, room, secret, base = CONNECT_URL) {
  const ws = base.replace(/^http/, "ws").replace(/\/+$/, "");
  return `${ws}/ws?room=${encodeURIComponent(room)}&key=${encodeURIComponent(secret)}&role=${role}`;
}
